import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { ArrowRight, FolderOpen, FileSpreadsheet, Scale, Search, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../../hooks/useApi";
import { useDashTheme } from "../hooks/useDashTheme";
import { PageShell } from "../components/PageShell";
import { Avatar } from "../components/primitives";
import { DashAntd } from "../components/DashAntd";
import LivingToolDesk from "./LivingToolDesk";
import { KpiCell, LivingAntd, paper, Rule, Sheet } from "./living/ltChrome";
import { SkeletonRows } from "./graphical/drill/StoryChrome";

function errMsg(err, fallback) {
  return (err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || err.message
    || fallback;
}

const DESK_ORDER = ["Compliance", "Finance", "Leadership", "Sales", "Operations", "Team"];

function deskOf(position) {
  const p = String(position || "").toLowerCase();
  if (/compliance/.test(p)) return "Compliance";
  if (/finance|account/.test(p)) return "Finance";
  if (/sales/.test(p)) return "Sales";
  if (/operat/.test(p)) return "Operations";
  if (/founder|ceo|director|chief|partner|management/.test(p)) return "Leadership";
  return "Team";
}

function groupByDesk(rows) {
  const map = {};
  rows.forEach((m) => {
    const d = deskOf(m.position);
    if (!map[d]) map[d] = [];
    map[d].push(m);
  });
  return DESK_ORDER.filter((d) => map[d] && map[d].length).map((d) => ({ desk: d, people: map[d] }));
}

function LiveDot() {
  const t = useDashTheme();
  return (
    <span style={{
      width: 7,
      height: 7,
      borderRadius: 99,
      background: t.status.positive,
      boxShadow: `0 0 0 4px ${t.status.positiveSoft}`,
      animation: "lt-live 1.8s ease-in-out infinite",
    }}
    />
  );
}

function FeaturedBook({ person, onOpen }) {
  const t = useDashTheme();
  const p = paper(t);
  const ink = t.c.primary;
  const headBg = t.isDark
    ? `color-mix(in srgb, ${ink} 12%, ${p.well})`
    : `color-mix(in srgb, ${ink} 8%, ${p.well})`;
  return (
    <Sheet hover onClick={onOpen} style={{ height: "100%" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "12px 20px",
        background: headBg,
        borderBottom: `1px solid ${t.c.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LiveDot />
          <span style={{ ...t.font.statLabel, color: ink }}>Live book</span>
        </div>
        {person.pending
          ? <span style={{ fontSize: 12, fontWeight: 700, color: t.status.warning, fontVariantNumeric: "tabular-nums" }}>{person.pending} pending</span>
          : <span style={{ fontSize: 12, fontWeight: 650, color: t.status.positive }}>In use</span>}
      </div>
      <div style={{ padding: "22px 22px 20px", display: "flex", gap: 18, alignItems: "center" }}>
        <Avatar user={person} name={person.name} size={72} highlight />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 650, letterSpacing: "-0.4px", lineHeight: 1.15 }}>{person.name}</div>
          <div style={{ ...t.font.statLabel, color: ink, marginTop: 6 }}>{person.position || "Member"}</div>
          <div style={{ marginTop: 10, fontSize: 13.5, color: t.c.textSecondary, lineHeight: 1.45, maxWidth: 420 }}>
            {/compliance/i.test(person.position || "")
              ? "Pre-clearance, grey list, the month’s SEBI pack — the desk this book is for."
              : "A book built from their work. Open it and use it."}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
            {["Pre-clearance", "Grey list", "SEBI pack"].map((c) => (
              <span key={c} style={{
                fontSize: 11,
                fontWeight: 650,
                padding: "3px 9px",
                borderRadius: 99,
                background: p.well,
                color: t.c.textSecondary,
              }}
              >{c}</span>
            ))}
          </div>
        </div>
        <Button type="primary" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          Open the book <ArrowRight size={14} style={{ marginLeft: 6, verticalAlign: "-2px" }} />
        </Button>
      </div>
    </Sheet>
  );
}

function PersonCard({ person, onOpen }) {
  const t = useDashTheme();
  const pair = t.accentFor(deskOf(person.position));
  return (
    <Sheet hover pad="16px 16px 14px" rail={pair.bg} onClick={onOpen} style={{ height: "100%" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <Avatar user={person} name={person.name} size={42} />
        <ArrowRight size={15} color={t.c.textTertiary} style={{ marginTop: 4, flexShrink: 0 }} />
      </div>
      <div style={{ marginTop: 12, fontWeight: 650, fontSize: 14.5, letterSpacing: "-0.2px", lineHeight: 1.25 }}>{person.name}</div>
      <div style={{ marginTop: 4, fontSize: 12, color: t.c.textTertiary, lineHeight: 1.4 }}>{person.position || "Member"}</div>
      <div style={{
        marginTop: 12,
        paddingTop: 10,
        borderTop: `1px solid ${t.c.border}`,
        ...t.font.microLabel,
        color: t.c.primary,
      }}>
        Open book
      </div>
    </Sheet>
  );
}

export default function LivingToolV2() {
  const t = useDashTheme();
  const api = useApi();
  const [params, setParams] = useSearchParams();
  const userId = params.get("userId");
  const [tab, setTab] = useState("queue");

  const [roster, setRoster] = useState({ loading: true, error: null, members: [], liveCount: 0 });
  const [q, setQ] = useState("");
  const [tool, setTool] = useState({ loading: false, error: null, data: null });

  const loadRoster = useCallback(async () => {
    setRoster((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.get("/api/v1/living-tool/roster");
      const d = (res.data && res.data.data) || {};
      setRoster({ loading: false, error: null, members: d.members || [], liveCount: d.liveCount || 0 });
    } catch (err) {
      setRoster({ loading: false, error: errMsg(err, "Could not load people"), members: [], liveCount: 0 });
    }
  }, [api]);

  useEffect(() => { if (!userId) loadRoster(); }, [loadRoster, userId]);

  useEffect(() => {
    if (!userId) {
      setTool({ loading: false, error: null, data: null });
      setTab("queue");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setTool({ loading: true, error: null, data: null });
      try {
        const res = await api.get(`/api/v1/living-tool?userId=${encodeURIComponent(userId)}`);
        let d = res.data && res.data.data;
        if (d && d.empty) {
          const en = await api.post("/api/v1/living-tool/enable", { userId });
          d = en.data && en.data.data;
        }
        if (!cancelled) setTool({ loading: false, error: null, data: d });
      } catch (err) {
        if (!cancelled) setTool({ loading: false, error: errMsg(err, "Could not open tool"), data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [api, userId]);

  const mutate = async (method, url, body) => {
    const res = await api[method](url, body);
    setTool({ loading: false, error: null, data: res.data && res.data.data });
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const rows = roster.members;
    if (!s) return rows;
    return rows.filter((m) => `${m.name} ${m.position} ${m.email}`.toLowerCase().includes(s));
  }, [roster.members, q]);

  const live = filtered.filter((m) => m.live || m.pending);
  const rest = filtered.filter((m) => !m.live && !m.pending);

  const profile = tool.data && tool.data.profile;
  const desk = tool.data && tool.data.desk;
  const counts = (desk && desk.counts) || {};
  const ic = { size: 14, strokeWidth: 2.2 };

  if (userId) {
    return (
      <PageShell
        eyebrow="Living tool"
        title={profile && profile.subject ? profile.subject.name : "The book"}
        subtitle={profile && profile.subject
          ? `${profile.subject.position || "Member"} · the book she keeps`
          : "Opening the book"}
        actions={
          <DashAntd>
            <Button icon={<ArrowLeftOutlined />} onClick={() => { setParams({}); setTool({ loading: false, error: null, data: null }); }}>
              People
            </Button>
          </DashAntd>
        }
        tabs={[
          { key: "queue", label: "Pre-clearance", count: counts.pending || 0, icon: <Scale {...ic} /> },
          { key: "grey", label: "Grey list", count: counts.greyActive || 0, icon: <ShieldAlert {...ic} /> },
          { key: "pack", label: "SEBI pack", icon: <FileSpreadsheet {...ic} /> },
          { key: "files", label: "Open files", count: counts.filesOpen || 0, icon: <FolderOpen {...ic} /> },
        ]}
        activeTab={tab}
        onTabChange={setTab}
        tabLayoutId="livingToolTab"
      >
        <div style={{ maxWidth: t.layout.contentMax }}>
          <DashAntd>
            {tool.loading && <SkeletonRows count={5} height={56} />}
            {tool.error && (
              <Sheet pad={20}>
                <div style={{ color: t.status.negative, fontWeight: 600 }}>{tool.error}</div>
              </Sheet>
            )}
            {!tool.loading && profile && desk && (
              <LivingToolDesk
                tab={tab}
                desk={desk}
                userId={userId}
                canAct={!!(tool.data && tool.data.canAct)}
                onMutate={mutate}
              />
            )}
          </DashAntd>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      eyebrow="Living tool"
      title="The books"
      subtitle={roster.loading
        ? "Loading the org…"
        : `${roster.members.length} people · ${roster.liveCount} live`}
    >
      <DashAntd>
        <LivingAntd>
          <style>{`@keyframes lt-live { 0%,100% { opacity: 1 } 50% { opacity: .35 } }`}</style>
          <div style={{ maxWidth: t.layout.contentMax }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
              <div style={{ maxWidth: 560, fontSize: 15, lineHeight: 1.5, color: t.c.textSecondary }}>
                Each person gets a book from their work. Open one. Use it.
              </div>
              <Input
                allowClear
                prefix={<Search size={15} color={t.c.icon} />}
                placeholder="Find someone"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                style={{ width: 300 }}
              />
            </div>

            {roster.loading && <SkeletonRows count={6} height={72} />}
            {roster.error && (
              <Sheet pad={20}>
                <div style={{ color: t.status.negative, fontWeight: 600 }}>{roster.error}</div>
              </Sheet>
            )}

            {!roster.loading && !filtered.length && (
              <Sheet pad={28}>
                <div style={{ color: t.c.textTertiary }}>No one matches.</div>
              </Sheet>
            )}

            {live.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1.7fr) minmax(200px, 0.7fr)",
                gap: 12,
                marginBottom: 28,
              }}>
                <FeaturedBook person={live[0]} onOpen={() => setParams({ userId: String(live[0].userId) })} />
                <div style={{ display: "grid", gap: 10, alignContent: "stretch" }}>
                  <KpiCell label="In the org" value={roster.members.length} sub="people" />
                  <KpiCell label="Live books" value={roster.liveCount} sub="already in use" color={t.c.primary} />
                  <KpiCell label="Ready" value={rest.length} sub="a book is waiting" />
                </div>
              </div>
            )}

            {live.slice(1).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12, marginBottom: 24 }}>
                {live.slice(1).map((m) => (
                  <PersonCard key={String(m.userId)} person={m} onOpen={() => setParams({ userId: String(m.userId) })} />
                ))}
              </div>
            )}

            {groupByDesk(rest).map((g) => (
              <motion.div key={g.desk} {...t.stagger.container} style={{ marginBottom: 26 }}>
                <Rule title={g.desk} hint={`${g.people.length} ${g.people.length === 1 ? "book" : "books"}`} />
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                  {g.people.map((m) => (
                    <motion.div key={String(m.userId)} {...t.stagger.item}>
                      <PersonCard person={m} onOpen={() => setParams({ userId: String(m.userId) })} />
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </LivingAntd>
      </DashAntd>
    </PageShell>
  );
}
